/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useCallback } from "react";

import { faClock } from "@fortawesome/pro-light-svg-icons";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import { PickerTimeProps } from "antd/es/date-picker/generatePicker";
import cn from "classnames";
import { getHours, getMinutes, getSeconds, set } from "date-fns";

import ErrorRow from "../ErrorRow";
import Labeled, { LabeledProps } from "../Labeled";
import useDatePicker from "../util/useDatePicker";

const block = bem("mmc-time-picker");

export interface TimePickerProps
  extends Omit<PickerTimeProps<Date>, "picker">,
    Omit<LabeledProps, "children" | "suffix"> {
  caption?: string;
  className?: string;
  error?: ReactNode;
  locked?: boolean;
  use12Hours?: boolean;
}

const TimePicker = React.forwardRef<any, TimePickerProps>(
  (
    {
      caption,
      className,
      colProps,
      disabled,
      error,
      extra,
      label,
      labelClassName,
      labelPosition,
      locked,
      onChange,
      required,
      rowProps,
      sideLabelSpan,
      size = "large",
      value,
      ...props
    },
    ref
  ) => {
    const DatePickerComponent = useDatePicker();

    const handleTimeChange = useCallback(
      (date: Date | null, dateString: string) => {
        if (!date) {
          onChange?.(date, dateString);
          return;
        }

        onChange?.(
          set(value ?? new Date(), {
            hours: getHours(date),
            minutes: getMinutes(date),
            seconds: getSeconds(date),
          }),
          dateString
        );
      },
      [onChange, value]
    );

    return (
      <Labeled
        className={cn(block({ disabled: disabled || locked, [size]: true }), className)}
        colProps={colProps}
        extra={extra}
        label={label}
        labelClassName={cn(block("label"), labelClassName)}
        labelPosition={labelPosition}
        required={required}
        rowProps={rowProps}
        sideLabelSpan={sideLabelSpan}
      >
        <div className={block("input")}>
          <DatePickerComponent
            disabled={disabled || locked}
            mode={undefined}
            onChange={handleTimeChange}
            picker="time"
            ref={ref}
            size={size}
            suffixIcon={
              locked ? (
                <div className={block("lock")}>
                  <FontAwesomeIcon icon={faLock} size="xs" />
                </div>
              ) : (
                <FontAwesomeIcon className={block("clock")} icon={faClock} />
              )
            }
            value={value}
            {...props}
          />
          {error ? (
            <ErrorRow>{error}</ErrorRow>
          ) : caption ? (
            <div className={block("caption")}>{caption}</div>
          ) : null}
        </div>
      </Labeled>
    );
  }
);

TimePicker.displayName = "TimePicker";

export default TimePicker;
